import { MessageCircleQuestionMark } from 'lucide-react';
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Select,
} from '@/components/ui';
import { useBranch } from '@/features/branch/branch-context';
import { usePatientQuestions } from '@/features/patient-questions/api';
import {
  QUESTION_STATUSES,
  type QuestionStatusFilter,
} from '@/features/patient-questions/types';
import { PatientQuestionDrawer } from './PatientQuestionDrawer';
import {
  lastActivity,
  QUESTION_STATUS_LABEL,
  QUESTION_STATUS_TONE,
} from './question-format';
import { when } from './rx-request-format';
import styles from './PatientQuestions.module.css';

const STATUS_OPTIONS = [
  ...QUESTION_STATUSES.map((s) => ({
    value: s,
    label: QUESTION_STATUS_LABEL[s],
  })),
  { value: 'all', label: 'All' },
];

/**
 * "Questions from the app" (RL-1139): patients' questions about a released
 * result (RL-991). The doctor sees those routed to them plus the branch's
 * unrouted ones, with the text; the manager sees the branch without the text
 * (`textShown: false`) and routes them to a doctor.
 */
export function PatientQuestionsTab({ oversight }: { oversight: boolean }) {
  const { selected, canSelectAll } = useBranch();
  const [status, setStatus] = useState<QuestionStatusFilter>('open');
  const [openId, setOpenId] = useState<string | null>(null);
  const q = usePatientQuestions(status);
  const noBranch = !selected && !canSelectAll;

  return (
    <Card
      title="Questions from the app"
      flush
      actions={
        <div className={styles.filters}>
          <Field label="Status">
            <Select
              value={status}
              options={STATUS_OPTIONS}
              onChange={(e) =>
                setStatus(e.target.value as QuestionStatusFilter)
              }
            />
          </Field>
        </div>
      }
    >
      {oversight && (
        <p className={styles.oversightNote}>
          Oversight view: the questions, replies and result values are shown
          only to the doctor who answers them. You can route an open question to
          a doctor of its branch.
        </p>
      )}
      <DataTable
        caption="Questions from the app about a result"
        rows={noBranch ? [] : q.data}
        rowKey={(r) => r.id}
        loading={!noBranch && q.isLoading}
        error={noBranch ? undefined : q.error}
        onRetry={() => void q.refetch()}
        empty={
          <EmptyState
            icon={<MessageCircleQuestionMark size={22} />}
            title={
              noBranch
                ? 'Choose a branch to see its questions'
                : status === 'open'
                  ? 'No questions waiting'
                  : 'No questions match this filter'
            }
            body={
              !noBranch && status === 'open'
                ? 'Questions patients ask in the app about a released result appear here.'
                : undefined
            }
          />
        }
        columns={[
          {
            key: 'patient',
            header: 'Patient',
            render: (r) => <strong>{r.patient.fullName}</strong>,
          },
          {
            key: 'result',
            header: 'About',
            render: (r) =>
              r.subject ? (
                <span className={styles.about}>
                  {r.subject.testName}
                  {r.subject.orderNumber && (
                    <span className={`${styles.muted} mono`}>
                      {r.subject.orderNumber}
                    </span>
                  )}
                </span>
              ) : (
                <span className={styles.muted}>—</span>
              ),
          },
          {
            key: 'messages',
            header: 'Messages',
            align: 'right',
            render: (r) => r.messageCount ?? '—',
          },
          {
            key: 'activity',
            header: 'Waiting since / last message',
            render: (r) => when(lastActivity(r)),
          },
          {
            key: 'routed',
            header: 'Routed to',
            render: (r) => r.routedTo?.fullName ?? 'Branch doctors',
          },
          {
            key: 'status',
            header: 'Status',
            render: (r) => (
              <Badge tone={QUESTION_STATUS_TONE[r.status]} dot>
                {QUESTION_STATUS_LABEL[r.status]}
              </Badge>
            ),
          },
          {
            key: 'open',
            header: <span className="visually-hidden">Actions</span>,
            align: 'right',
            render: (r) => (
              <Button
                size="sm"
                onClick={() => setOpenId(r.id)}
                aria-label={`Open the question from ${r.patient.fullName}`}
              >
                Open
              </Button>
            ),
          },
        ]}
      />
      {openId && (
        <PatientQuestionDrawer id={openId} onClose={() => setOpenId(null)} />
      )}
    </Card>
  );
}
